import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Search, Upload, LogOut, Film, LayoutGrid, Users, Sparkles } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export const Navbar = ({ onSearchOpen, onUploadOpen, dark = false }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const NavLink = ({ to, icon: Icon, label }) => {
    const active = loc.pathname.startsWith(to);
    return (
      <Link
        to={to}
        data-testid={`nav-${label.toLowerCase()}`}
        className={`group flex items-center gap-2 px-3 py-2 text-sm font-medium tracking-tight transition-colors ${
          active
            ? dark ? "text-white" : "text-black"
            : dark ? "text-white/60 hover:text-white" : "text-black/55 hover:text-black"
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={1.5} />
        <span>{label}</span>
        {active && <span className="ml-1 w-1.5 h-1.5 rounded-full bg-[#E60000]" />}
      </Link>
    );
  };

  return (
    <header className={`${dark ? "glass-dark" : "glass-light"} sticky top-0 z-40`}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/library" className="shrink-0">
          <Logo inverted={dark} />
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <NavLink to="/library" icon={LayoutGrid} label="Library" />
          <NavLink to="/reels" icon={Film} label="Reels" />
          <NavLink to="/workspace" icon={Users} label="Squad" />
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onSearchOpen}
            data-testid="ai-search-trigger"
            className={`hidden md:flex items-center gap-3 h-10 px-3 min-w-[280px] border ${
              dark ? "border-white/15 hover:border-white/30 text-white/70 bg-white/5"
                   : "border-black/15 hover:border-black/40 text-black/55 bg-white"
            } transition-colors`}
          >
            <Sparkles className="w-4 h-4 text-[#E60000]" strokeWidth={1.5} />
            <span className="text-sm flex-1 text-left">Ask Monster Squad…</span>
            <kbd className={`text-[10px] tracking-wider px-1.5 py-0.5 border ${dark ? "border-white/20 text-white/60" : "border-black/15 text-black/50"}`}>⌘ K</kbd>
          </button>

          <Button
            onClick={onSearchOpen}
            data-testid="ai-search-mobile"
            variant="ghost"
            size="icon"
            className="md:hidden"
          >
            <Search className="w-5 h-5" />
          </Button>

          <Button
            onClick={onUploadOpen}
            data-testid="upload-button"
            className="rounded-sm bg-[#E60000] hover:bg-[#CC0000] text-white font-medium tracking-tight gap-2"
          >
            <Upload className="w-4 h-4" strokeWidth={2} />
            <span className="hidden sm:inline">Upload</span>
          </Button>

          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-avatar-trigger" className="ml-1 relative">
                  <Avatar className="w-9 h-9 rounded-sm border border-black/10">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="rounded-sm bg-black text-white text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() || "M"}
                    </AvatarFallback>
                  </Avatar>
                  {(user.role === "owner" || user.is_premium) && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#E60000] border border-white" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm w-56" data-testid="user-menu">
                <DropdownMenuLabel className="font-heading">
                  <div className="font-bold truncate">{user.name}</div>
                  <div className="text-xs text-muted-foreground font-normal truncate">{user.email}</div>
                  <div className="mt-1.5">
                    {user.role === "owner" ? (
                      <span className="text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 bg-[#E60000] text-white">Owner</span>
                    ) : user.is_premium ? (
                      <span className="text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 bg-black text-white">Premium</span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-[0.2em] font-bold px-1.5 py-0.5 border border-black/20 text-black/60">Free</span>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/workspace")} data-testid="menu-workspace">
                  <Users className="w-4 h-4 mr-2" /> Squad workspace
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
};
